/**
 * Kiosko de Turnos - Self-service kiosk for patients
 *
 * Public page (no auth) for tablets in waiting rooms.
 * Patients select a service type and get a printed ticket.
 * - 3 themes: Light, Dark, Green (same as pantalla-publica)
 * - 3 languages: es, en, pt via i18next
 * - All turns created with BAJA priority
 * - Print ticket after creation
 * - Touch-friendly large buttons
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Palette,
  Maximize,
  Minimize,
  Printer,
  FlaskConical,
  FileText,
  Calculator,
  HelpCircle,
  CheckCircle,
  RotateCcw,
  Loader2,
  Globe,
} from "lucide-react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import { usePantallaPublica, useCrearTurnoPublico, type Turno } from "@/hooks/use-turnos";

type ThemeKey = "light" | "dark" | "green";
interface Theme {
  pageBg: string;
  headerBg: string;
  cardBg: string;
  footerBg: string;
  headerText: string;
  titleText: string;
  subtitleText: string;
  numberText: string;
  mutedText: string;
  buttonBg: string;
  buttonHover: string;
  borderAccent: string;
  serviceBg: string;
  serviceHover: string;
  serviceBorder: string;
  serviceText: string;
  serviceIconColor: string;
  successBg: string;
  successText: string;
}

const themes: Record<ThemeKey, Theme> = {
  light: {
    pageBg: "bg-slate-300",
    headerBg: "bg-emerald-700",
    cardBg: "bg-slate-200",
    footerBg: "bg-slate-300",
    headerText: "text-white",
    titleText: "text-slate-700",
    subtitleText: "text-slate-500",
    numberText: "text-emerald-700",
    mutedText: "text-slate-400",
    buttonBg: "bg-emerald-600",
    buttonHover: "hover:bg-emerald-500",
    borderAccent: "border-emerald-500",
    serviceBg: "bg-white",
    serviceHover: "hover:bg-emerald-50 hover:border-emerald-500",
    serviceBorder: "border-slate-300",
    serviceText: "text-slate-700",
    serviceIconColor: "text-emerald-600",
    successBg: "bg-emerald-50",
    successText: "text-emerald-700",
  },
  dark: {
    pageBg: "bg-slate-900",
    headerBg: "bg-slate-800",
    cardBg: "bg-slate-800",
    footerBg: "bg-slate-900",
    headerText: "text-white",
    titleText: "text-white",
    subtitleText: "text-slate-400",
    numberText: "text-emerald-400",
    mutedText: "text-slate-500",
    buttonBg: "bg-slate-700",
    buttonHover: "hover:bg-slate-600",
    borderAccent: "border-emerald-500",
    serviceBg: "bg-slate-700",
    serviceHover: "hover:bg-slate-600 hover:border-emerald-500",
    serviceBorder: "border-slate-600",
    serviceText: "text-slate-200",
    serviceIconColor: "text-emerald-400",
    successBg: "bg-emerald-900/50",
    successText: "text-emerald-400",
  },
  green: {
    pageBg: "bg-emerald-900",
    headerBg: "bg-emerald-950",
    cardBg: "bg-emerald-800",
    footerBg: "bg-emerald-900",
    headerText: "text-white",
    titleText: "text-white",
    subtitleText: "text-emerald-300",
    numberText: "text-white",
    mutedText: "text-emerald-400",
    buttonBg: "bg-emerald-700",
    buttonHover: "hover:bg-emerald-600",
    borderAccent: "border-emerald-400",
    serviceBg: "bg-emerald-700",
    serviceHover: "hover:bg-emerald-600 hover:border-yellow-400",
    serviceBorder: "border-emerald-600",
    serviceText: "text-emerald-100",
    serviceIconColor: "text-emerald-300",
    successBg: "bg-emerald-700",
    successText: "text-white",
  },
};
const themeOrder: ThemeKey[] = ["light", "dark", "green"];

type LangKey = "es" | "en" | "pt";
const LANGUAGES: { key: LangKey; label: string; flag: string }[] = [
  { key: "es", label: "Español", flag: "🇪🇸" },
  { key: "en", label: "English", flag: "🇺🇸" },
  { key: "pt", label: "Português", flag: "🇧🇷" },
];

const SERVICE_OPTIONS = [
  { value: "examenes", labelKey: "kiosko.services.exams", icon: FlaskConical },
  { value: "resultados", labelKey: "kiosko.services.results", icon: FileText },
  { value: "cotizacion", labelKey: "kiosko.services.quote", icon: Calculator },
  { value: "otro", labelKey: "kiosko.services.other", icon: HelpCircle },
];

const LOCALE_MAP: Record<LangKey, string> = { es: "es-HN", en: "en-US", pt: "pt-BR" };

type KioskoState = "select" | "creating" | "success" | "error";

export default function KioskoTurnos() {
  const { sucursalCodigo } = useParams<{ sucursalCodigo: string }>();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<KioskoState>("select");
  const [createdTurno, setCreatedTurno] = useState<Turno | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<ThemeKey>("light");
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [countdown, setCountdown] = useState(15);

  const ticketRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: ticketRef,
    documentTitle: `Turno-${createdTurno?.numero || ""}`,
  });

  const currentLang = (i18n.language?.substring(0, 2) || "es") as LangKey;
  const currentLangInfo = LANGUAGES.find((l) => l.key === currentLang) || LANGUAGES[0];
  const dateLocale = LOCALE_MAP[currentLang] || "es-HN";

  // Get sucursal name via hook (no auth required)
  const { data: pantallaData } = usePantallaPublica(sucursalCodigo ?? "", {
    enabled: !!sucursalCodigo,
  });
  const sucursalNombre = pantallaData?.sucursal?.nombre ?? "";

  const crearTurnoMutation = useCrearTurnoPublico({
    onSuccess: (turno) => {
      setCreatedTurno(turno);
      setState("success");
    },
    onError: (err) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setErrorMsg(axiosErr?.response?.data?.detail || t("kiosko.errorCreating"));
      setState("error");
    },
  });

  useEffect(() => {
    if (sucursalCodigo) {
      const saved = localStorage.getItem(`kiosko-tema-${sucursalCodigo}`);
      if (saved && themeOrder.includes(saved as ThemeKey)) setCurrentTheme(saved as ThemeKey);
    }
  }, [sucursalCodigo]);

  useEffect(() => {
    if (sucursalCodigo) {
      const saved = localStorage.getItem(`kiosko-lang-${sucursalCodigo}`);
      if (saved && LANGUAGES.some((l) => l.key === saved)) i18n.changeLanguage(saved);
    }
  }, [sucursalCodigo, i18n]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node))
        setShowLangMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const theme = themes[currentTheme];

  const cycleTheme = useCallback(() => {
    const idx = themeOrder.indexOf(currentTheme);
    const next = themeOrder[(idx + 1) % themeOrder.length];
    setCurrentTheme(next);
    if (sucursalCodigo) localStorage.setItem(`kiosko-tema-${sucursalCodigo}`, next);
  }, [currentTheme, sucursalCodigo]);

  const changeLanguage = useCallback(
    (lang: LangKey) => {
      i18n.changeLanguage(lang);
      if (sucursalCodigo) localStorage.setItem(`kiosko-lang-${sucursalCodigo}`, lang);
      setShowLangMenu(false);
    },
    [i18n, sucursalCodigo]
  );

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
    else document.exitFullscreen();
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  useEffect(() => {
    if (state === "success") {
      setCountdown(15);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setState("select");
            setCreatedTurno(null);
            return 15;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [state]);

  const handleSelectService = (tipoServicio: string) => {
    if (!sucursalCodigo) return;
    setState("creating");
    setErrorMsg("");
    crearTurnoMutation.mutate({ sucursal_codigo: sucursalCodigo, tipo_servicio: tipoServicio });
  };

  const handleReset = () => {
    setState("select");
    setCreatedTurno(null);
    setErrorMsg("");
  };

  if (!sucursalCodigo) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center p-4`}>
        <p className={`${theme.subtitleText} text-2xl`}>{t("kiosko.branchNotSpecified")}</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.pageBg} flex flex-col`}>
      {/* Header */}
      <header
        className={`${theme.headerBg} ${theme.headerText} px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-5 shadow-lg`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-5 min-w-0">
            <img
              src="/logo-lab.png"
              alt="Logo"
              className="h-10 sm:h-14 lg:h-20 w-auto flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl lg:text-4xl font-bold truncate">
                {sucursalNombre || t("kiosko.labName")}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base opacity-80">{t("kiosko.title")}</p>
            </div>
          </div>
          <div className="flex gap-1 sm:gap-2 flex-shrink-0">
            {/* Language selector */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className={`p-2 sm:p-3 ${theme.buttonBg} ${theme.buttonHover} rounded-lg transition-colors flex items-center gap-1`}
                title={t("kiosko.language")}
              >
                <Globe className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">
                  {currentLangInfo.flag}
                </span>
              </button>
              {showLangMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[160px]">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.key}
                      onClick={() => changeLanguage(lang.key)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                        currentLang === lang.key
                          ? "bg-emerald-50 text-emerald-700 font-semibold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={cycleTheme}
              className={`p-2 sm:p-3 ${theme.buttonBg} ${theme.buttonHover} rounded-lg transition-colors`}
              title={t("kiosko.theme." + currentTheme)}
            >
              <Palette className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <button
              onClick={toggleFullscreen}
              className={`p-2 sm:p-3 ${theme.buttonBg} ${theme.buttonHover} rounded-lg transition-colors hidden sm:block`}
              title={isFullscreen ? t("kiosko.exitFullscreen") : t("kiosko.fullscreen")}
            >
              {isFullscreen ? (
                <Minimize className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Maximize className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        {state === "select" && (
          <div
            className={`w-full max-w-3xl ${theme.cardBg} rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10`}
          >
            <h2
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-2 ${theme.titleText}`}
            >
              {t("kiosko.selectService")}
            </h2>
            <p
              className={`text-center mb-8 sm:mb-10 ${theme.subtitleText} text-sm sm:text-base lg:text-lg`}
            >
              {t("kiosko.selectServiceDesc")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {SERVICE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleSelectService(opt.value)}
                  className={`flex flex-col items-center gap-3 sm:gap-4 p-6 sm:p-8 rounded-xl border-2 ${theme.serviceBg} ${theme.serviceBorder} ${theme.serviceHover} transition-all active:scale-95 cursor-pointer`}
                >
                  <opt.icon className={`h-10 w-10 sm:h-14 sm:w-14 ${theme.serviceIconColor}`} />
                  <span
                    className={`text-lg sm:text-xl lg:text-2xl font-semibold text-center ${theme.serviceText}`}
                  >
                    {t(opt.labelKey)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {state === "creating" && (
          <div className={`${theme.cardBg} rounded-2xl shadow-xl p-10 sm:p-16 text-center`}>
            <Loader2
              className={`h-16 w-16 sm:h-20 sm:w-20 ${theme.numberText} animate-spin mx-auto mb-6`}
            />
            <p className={`text-xl sm:text-2xl ${theme.titleText} font-semibold`}>
              {t("kiosko.generating")}
            </p>
          </div>
        )}

        {state === "success" && createdTurno && (
          <div
            className={`w-full max-w-lg ${theme.cardBg} rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 text-center`}
          >
            <div
              className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full ${theme.successBg} mb-4`}
            >
              <CheckCircle className={`h-10 w-10 sm:h-12 sm:w-12 ${theme.successText}`} />
            </div>
            <p className={`text-lg sm:text-xl ${theme.subtitleText} mb-2`}>
              {t("kiosko.yourTurn")}
            </p>
            <div className={`text-7xl sm:text-9xl font-bold ${theme.numberText} my-4`}>
              {String(createdTurno.numero).padStart(3, "0")}
            </div>
            {createdTurno.tipo_servicio && (
              <p className={`text-base sm:text-lg ${theme.subtitleText} capitalize mb-6`}>
                {createdTurno.tipo_servicio}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-6">
              <button
                onClick={() => handlePrint()}
                className={`flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 ${theme.buttonBg} ${theme.buttonHover} text-white rounded-xl text-lg sm:text-xl font-semibold transition-colors`}
              >
                <Printer className="h-5 w-5 sm:h-6 sm:w-6" />
                {t("kiosko.printTicket")}
              </button>
              <button
                onClick={handleReset}
                className={`flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border-2 ${theme.serviceBorder} ${theme.serviceText} rounded-xl text-lg sm:text-xl font-semibold transition-colors ${theme.serviceHover}`}
              >
                <RotateCcw className="h-5 w-5 sm:h-6 sm:w-6" />
                {t("kiosko.newTurn")}
              </button>
            </div>
            <p className={`text-sm ${theme.mutedText} mt-6`}>
              {t("kiosko.autoReset", { seconds: countdown })}
            </p>
          </div>
        )}

        {state === "error" && (
          <div
            className={`w-full max-w-lg ${theme.cardBg} rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 text-center`}
          >
            <p className="text-red-500 text-xl sm:text-2xl font-semibold mb-4">{errorMsg}</p>
            <button
              onClick={handleReset}
              className={`flex items-center justify-center gap-2 mx-auto px-8 py-4 ${theme.buttonBg} ${theme.buttonHover} text-white rounded-xl text-xl font-semibold transition-colors`}
            >
              <RotateCcw className="h-6 w-6" />
              {t("kiosko.tryAgain")}
            </button>
          </div>
        )}
      </div>

      {/* Hidden print ticket */}
      {createdTurno && (
        <div className="hidden">
          <div
            ref={ticketRef}
            className="w-[280px] bg-white p-4 font-mono text-black"
            style={{ fontFamily: "monospace" }}
          >
            <div className="text-center border-b-2 border-dashed border-gray-400 pb-3 mb-3">
              <h1 className="text-lg font-bold">{t("kiosko.ticket.title")}</h1>
              {sucursalNombre && <p className="text-sm">{sucursalNombre}</p>}
              <p className="text-xs text-gray-600 mt-1">
                {new Date(createdTurno.fecha).toLocaleDateString(dateLocale, {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="text-center py-4">
              <p className="text-sm text-gray-600 uppercase">{t("kiosko.ticket.yourTurn")}</p>
              <p className="text-6xl font-bold my-2">
                {String(createdTurno.numero).padStart(3, "0")}
              </p>
              <p className="text-sm font-semibold text-green-600">{t("kiosko.ticket.priority")}</p>
            </div>
            <div className="border-t-2 border-dashed border-gray-400 pt-3 mt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("kiosko.ticket.arrivalTime")}</span>
                <span className="font-semibold">
                  {createdTurno.hora_llegada?.slice(0, 5) || "--:--"}
                </span>
              </div>
              {createdTurno.tipo_servicio && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("kiosko.ticket.service")}</span>
                  <span className="font-semibold capitalize">{createdTurno.tipo_servicio}</span>
                </div>
              )}
            </div>
            <div className="text-center mt-4 pt-3 border-t-2 border-dashed border-gray-400">
              <p className="text-xs text-gray-500">{t("kiosko.ticket.waitMessage")}</p>
              <p className="text-xs text-gray-400 mt-2">
                {new Date().toLocaleTimeString(dateLocale)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer
        className={`px-4 sm:px-8 py-2 sm:py-3 text-center ${theme.mutedText} text-xs sm:text-sm ${theme.footerBg}`}
      >
        {sucursalNombre} • {t("kiosko.theme." + currentTheme)}
      </footer>
    </div>
  );
}
